import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"

interface Proposal {
  id: string
  title: string
  client_id: string
  value?: number | null
  final_value?: number | null
}

interface MultiSelectProposalsProps {
  proposals: Proposal[]
  selectedProposalIds: string[]
  onSelectionChange: (selectedIds: string[]) => void
  placeholder?: string
  clientFilter?: string
  disabled?: boolean
}

export function MultiSelectProposals({
  proposals,
  selectedProposalIds,
  onSelectionChange,
  placeholder = "Select proposals...",
  clientFilter,
  disabled = false
}: MultiSelectProposalsProps) {
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState("")
  
  // Ensure proposals and selectedProposalIds are always arrays
  const safeProposals = Array.isArray(proposals) ? proposals : []
  const safeSelectedIds = Array.isArray(selectedProposalIds) ? selectedProposalIds : []
  
  // Filter proposals by client and search term
  const filteredProposals = safeProposals.filter(proposal => {
    const matchesClient = !clientFilter || proposal.client_id === clientFilter
    const matchesSearch = !searchTerm || 
      proposal.title.toLowerCase().includes(searchTerm.toLowerCase())
    
    return matchesClient && matchesSearch
  })

  const selectedProposals = safeProposals.filter(proposal => safeSelectedIds.includes(proposal.id))

  const handleSelect = (proposalId: string) => {
    if (safeSelectedIds.includes(proposalId)) {
      onSelectionChange(safeSelectedIds.filter(id => id !== proposalId))
    } else {
      onSelectionChange([...safeSelectedIds, proposalId])
    }
  }

  const handleRemove = (proposalId: string) => {
    onSelectionChange(safeSelectedIds.filter(id => id !== proposalId))
  }

  const noProposalsMessage = !clientFilter 
    ? "Please select a client first" 
    : "No proposals available for this client"

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-background"
            disabled={disabled || !clientFilter}
          >
            {selectedProposals.length > 0
              ? `${selectedProposals.length} proposal${selectedProposals.length > 1 ? 's' : ''} selected`
              : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0 bg-background z-50" align="start">
          <div className="bg-background rounded-md">
            {/* Search input */}
            <div className="p-2 border-b pointer-events-auto">
              <Input
                placeholder="Search proposals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8"
              />
            </div>
            
            {/* Proposals list with ScrollArea */}
            <ScrollArea className="h-64">
              <div className="p-1">
                {filteredProposals.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {noProposalsMessage}
                  </div>
                ) : (
                  filteredProposals.map((proposal) => (
                      <div
                        key={proposal.id}
                        className="flex items-center px-3 py-2 cursor-pointer hover:bg-muted rounded-sm pointer-events-auto"
                        onClick={() => handleSelect(proposal.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            safeSelectedIds.includes(proposal.id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="truncate">{proposal.title}</span>
                        {(proposal.final_value || proposal.value) && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (€{(proposal.final_value || proposal.value)?.toLocaleString()})
                          </span>
                        )}
                      </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Selected proposals badges */}
      {selectedProposals.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedProposals.map((proposal) => (
            <Badge
              key={proposal.id}
              variant="secondary"
              className="text-xs px-2 py-1"
            >
              {proposal.title}
              <button
                type="button"
                onClick={() => handleRemove(proposal.id)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
